import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DrawnSignatureStrokes } from '@emr/shared';

import { Button } from '../components/primitives.tsx';
import { SIGNATURE_VIEW_HEIGHT, SIGNATURE_VIEW_WIDTH, strokesToPath } from './SignatureMark.tsx';

type Stroke = DrawnSignatureStrokes[number];
type Point = Stroke[number];

/**
 * A draw-your-signature widget: pointer events captured against an inline SVG (not a raster
 * `<canvas>`) so the result is the same compact vector form `@emr/shared`'s
 * `drawnSignatureStrokesSchema` stores — no rasterization step, no image dependency.
 *
 * Points are normalized to 0..1 against the drawing surface's own bounding box at capture time, via
 * `getBoundingClientRect` on every move — cheap for a signature-length gesture (dozens, not
 * thousands, of points) and correct regardless of the surface's actual on-screen pixel size, which
 * differs between a phone and a desktop. The surface itself is pinned to the same 3:1 aspect ratio
 * `SignatureMark.tsx`'s `viewBox` renders against, so a mark drawn here is not stretched the first
 * time it is displayed somewhere else.
 *
 * Pointer capture (`setPointerCapture`) keeps receiving move/up events even if the pointer leaves
 * the SVG's bounds mid-stroke — without it, a fast stroke that crosses the element's edge would
 * silently drop the rest of that stroke, which reads as a broken pen rather than a widget bug.
 */
export function SignatureCanvas({
  value,
  onChange,
}: {
  readonly value: DrawnSignatureStrokes;
  readonly onChange: (strokes: DrawnSignatureStrokes) => void;
}) {
  const { t } = useTranslation('signature');
  const surfaceRef = useRef<SVGSVGElement>(null);
  const [inProgress, setInProgress] = useState<Stroke | null>(null);

  const pointFromEvent = (event: React.PointerEvent<SVGSVGElement>): Point => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width === 0 || rect.height === 0) return [0, 0];
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    return [x, y];
  };

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setInProgress([pointFromEvent(event)]);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (inProgress === null) return;
    setInProgress([...inProgress, pointFromEvent(event)]);
  };

  const endStroke = () => {
    if (inProgress === null) return;
    if (inProgress.length > 0) onChange([...value, inProgress]);
    setInProgress(null);
  };

  const handleClear = () => {
    setInProgress(null);
    onChange([]);
  };

  const previewStrokes = inProgress === null ? value : [...value, inProgress];

  return (
    <div className="flex flex-col gap-2">
      <svg
        ref={surfaceRef}
        viewBox={`0 0 ${String(SIGNATURE_VIEW_WIDTH)} ${String(SIGNATURE_VIEW_HEIGHT)}`}
        role="img"
        aria-label={t('canvas.surfaceAriaLabel')}
        className="aspect-[3/1] w-full max-w-md touch-none rounded-control border border-field-line bg-field"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      >
        <path
          d={strokesToPath(previewStrokes)}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-ink"
        />
      </svg>
      <div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleClear}
          disabled={value.length === 0}
        >
          {t('canvas.clear')}
        </Button>
      </div>
    </div>
  );
}
